import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useCallback } from "react";

import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalFooter from "ds/components/FullScreenModal/Footer";
import Box from "ds/components/Box";
import Tile from "ds/components/Tile";
import { User, Briefcase } from "components/icons/generated";
import { AccountType, VcsProvider } from "types/generated";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import { stringIsRequired } from "utils/formValidators";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageVCS } from "hooks/useAnalytics/pages/vcs";
import FormFieldHelperText from "ds/components/Form/Field/HelperText";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";

import VCSIntegrationsDocumentationButton from "../../components/DocumentationButton";
import VCSIntegrationsBackButton from "../../components/BackButton";
import VCSIntegrationsContinueButton from "../../components/ContinueButton";
import { ManageGitHubEnterpriseWizardFormFields } from "./types";

type VCSIntegrationsManageGitHubEnterpriseWizardStepOwnerProps = {
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  loading?: boolean;
};

const VCSIntegrationsManageGitHubEnterpriseWizardStepOwner = ({
  goToNextStep,
  goToPreviousStep,
  loading,
}: VCSIntegrationsManageGitHubEnterpriseWizardStepOwnerProps) => {
  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageVCS.VCSIntegrations,
  });

  const { control, register, formState, handleSubmit } =
    useFormContext<ManageGitHubEnterpriseWizardFormFields>();

  const [ownerChoice, integrationChoice] = useWatch({
    name: ["ownerType", "integration"],
    control,
  });

  const trackWizardOwnershipContinueClick = useCallback(() => {
    trackSegmentAnalyticsEvent("GitHub Wizard - Ownership Continue Click", {
      ownership: ownerChoice,
      type: integrationChoice,
    });
  }, [integrationChoice, ownerChoice, trackSegmentAnalyticsEvent]);

  const handleGoToNextStep = useCallback(() => {
    goToNextStep();
    trackWizardOwnershipContinueClick();
  }, [goToNextStep, trackWizardOwnershipContinueClick]);

  return (
    <>
      <FullScreenModalBody gap="x-large">
        <FullScreenModalBodyHeader
          title="Do you want the app to be owned by your personal GitHub account or by an organization?"
          noMargin
        />

        <Controller
          name="ownerType"
          control={control}
          rules={{ required: "Please select an option" }}
          render={({ field, fieldState }) => (
            <Box direction="column" gap="large" fullWidth>
              <Box fullWidth gap="large">
                <Tile
                  icon={User}
                  title="Personal"
                  indicator="radio"
                  selected={field.value === AccountType.User}
                  onClick={() => field.onChange(AccountType.User)}
                />

                <Tile
                  icon={Briefcase}
                  title="Organization"
                  indicator="radio"
                  selected={field.value === AccountType.Org}
                  onClick={() => field.onChange(AccountType.Org)}
                />
              </Box>
              {fieldState.error && (
                <FormFieldHelperText error={fieldState.error.message} id="ownerType" />
              )}
            </Box>
          )}
        />

        {ownerChoice === AccountType.Org && (
          <FormField
            label="GitHub Organization name"
            error={formState.errors?.ownerName?.message}
            helperText="The name is case-sensitive"
            noMargin
          >
            {({ ariaInputProps }) => (
              <Input
                placeholder="Enter Organization name here"
                error={!!formState.errors?.ownerName}
                {...register("ownerName", {
                  validate: stringIsRequired("Organization name field is required."),
                })}
                {...ariaInputProps}
              />
            )}
          </FormField>
        )}
      </FullScreenModalBody>
      <FullScreenModalFooter justify="between">
        <VCSIntegrationsDocumentationButton provider={VcsProvider.GithubEnterprise} />

        <Box gap="medium">
          <VCSIntegrationsBackButton onClick={goToPreviousStep} />

          <VCSIntegrationsContinueButton
            onClick={handleSubmit(handleGoToNextStep)}
            loading={loading}
          />
        </Box>
      </FullScreenModalFooter>
    </>
  );
};

export default VCSIntegrationsManageGitHubEnterpriseWizardStepOwner;
